import { FilterModel, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injectable, signal } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ExportsMonitoringChannelDrilldownExport } from 'exports/interfaces/exports-monitoring-channel.interface';
import { Observable, map, of } from 'rxjs';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services';

@Injectable({
    providedIn: 'root',
})
export class ExportsMonitoringChannelDrilldownService implements GridService {
    private apollo: ApolloBase;
    NAME = 'ExportsMonitoringChannelDrilldownService';

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
    ) {
        this.apollo = this.apolloProvider.use('distributionManagementAPI');
    }

    channelId = signal<string | null>(null);

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        const orderBy = params.request.sortModel[0]?.colId ?? 'name';
        const orderDirection = params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC';

        const channelId = this.channelId();
        if (!channelId) {
            return of({ data: [], totalCount: 0 });
        }

        return this.apollo
            .query({
                query: gql`
                    query getChannelExportInformationItems(
                        $listingName: String
                        $supplierName: String
                        $brandName: String
                        $minSuccessRate: Int
                        $maxSuccessRate: Int
                        $exportDate: ExportImportDateEnum
                        $exportType: ExportTypeEnum
                        $pageNumber: Int
                        $pageSize: Int
                        $channelExportInformationId: Guid
                        $orderBy: String
                        $orderDirection: OrderDirection
                    ) {
                        channelInformation {
                            searchChannelExportInformationItems(
                                searchQuery: {
                                    pageNumber: $pageNumber
                                    pageSize: $pageSize
                                    orderBy: $orderBy
                                    orderDirection: $orderDirection
                                }
                                channelExportInformationItemFilter: {
                                    listingName: $listingName
                                    supplierName: $supplierName
                                    brandName: $brandName
                                    minSuccessRate: $minSuccessRate
                                    maxSuccessRate: $maxSuccessRate
                                    exportDate: $exportDate
                                    exportType: $exportType
                                    channelExportInformationId: $channelExportInformationId
                                }
                            ) {
                                data {
                                    id
                                    channelExportInformationId
                                    brandId
                                    brandName
                                    listingId
                                    listingName
                                    supplierId
                                    supplierName
                                    exportDate
                                    exportDateOutput
                                    exportType
                                    successRate
                                }
                                totalCount
                            }
                        }
                    }
                `,
                variables: {
                    pageNumber,
                    pageSize,
                    listingName: (params.request.filterModel as FilterModel)['listingName']?.filter,
                    supplierName: (params.request.filterModel as FilterModel)['supplierName']?.filter,
                    brandName: (params.request.filterModel as FilterModel)['brandName']?.filter,
                    minSuccessRate: (params.request.filterModel as FilterModel)['successRate']?.filter,
                    maxSuccessRate: (params.request.filterModel as FilterModel)['successRate']?.filterTo,
                    exportDate: (params.request.filterModel as FilterModel)['exportDate']?.filter,
                    exportType: (params.request.filterModel as FilterModel)['exportType']?.filter,
                    channelExportInformationId: channelId,
                    orderBy,
                    orderDirection,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.channelInformation.searchChannelExportInformationItems;
                    const exportsData: ExportsMonitoringChannelDrilldownExport[] = returnValue.data.map((element: any) => {
                        const exportData: ExportsMonitoringChannelDrilldownExport = {
                            id: element.id,
                            listingName: element.listingName,
                            listingId: element.listingId,
                            supplierName: element.supplierName,
                            companyId: element.supplierId,
                            exportDate: element.exportDate,
                            exportType: element.exportType,
                            successRate: element.successRate,
                            brandName: element.brandName,
                            brandId: element.brandId,
                            group: true,
                        };
                        return exportData;
                    });
                    return { data: exportsData, totalCount: returnValue.totalCount };
                }),
            );
    }
}
